import React from 'react'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from './style'
import WireDiagram from 'components/WireDetails/Diagram'
import { addWireAction } from 'actions/entityActions'
import { updateUiAction } from 'actions/uiActions'
import { notificationAction } from 'actions/notificationActions'
import { DEFAULT_WIRE_OPTIONS, WIRE_FOLLOWER_OPTIONS, WIRE_SNAP_ICON, CAMERA_SELECTED_COLORS } from 'helpers/globals'
import getMapProjection from 'helpers/map/getMapProjection'
import snapToClosestObject from 'helpers/wires/snapToClosestObject'
import { connectedNetworkClosetSelector, currentEntityWireSelector, networkClosetExistsSelector, networkClosetsArraySelector } from 'selectors/components'

const mapStateToProps = state => {
  const { map: { ref }, ui: { drawingWire, highlightAddNetworkCloset } } = state
  const networkClosets = networkClosetsArraySelector(state)

  return {
    networkCloset: connectedNetworkClosetSelector(state),
    wire: currentEntityWireSelector(state),
    networkClosetExists: networkClosetExistsSelector(state),
    map: ref,
    drawingWire,
    highlightAddNetworkCloset,
    onlyNetworkCloset: networkClosets.length === 1 && networkClosets[0],
    networkClosets
  }
}

class WiringPanel extends React.Component {
  constructor(props) {
    super(props)
    this.markerDragListeners = []
    this.mapContainer = null
    this.moveListener = null
    this.clickListener = null
    this.downListener = null
    this.skipEventListener = null
    this.dragListener = null
    this.markups = []
    this.state = { dragging: false }
  }

  componentDidMount = _ => {
    const { startDeviceType, startDevice } = this.props

    if (startDeviceType === 'camera' && startDevice.markers) this.addDragListeners(startDevice.markers.camera)
    else if (startDeviceType !== 'camera' && startDevice.fillMarker) this.addDragListeners(startDevice.fillMarker)
  }

  componentDidUpdate = prevProps => {
    const { startDeviceType, startDevice } = this.props

    if (startDeviceType === 'camera') {
      const { markers } = startDevice
      const prevMarkers = prevProps.startDevice.markers

      if (markers && (!prevMarkers || markers.camera !== prevMarkers.camera)) this.addDragListeners(markers.camera)
    } else if (startDevice.fillMarker && !prevProps.startDevice.fillMarker) {
      this.addDragListeners(startDevice.fillMarker)
    }
  }

  addDragListeners = marker => {
    this.markerDragListeners.push(google.maps.event.addListener(marker, 'dragstart', _ => this.setState({ dragging: true })))
    this.markerDragListeners.push(google.maps.event.addListener(marker, 'dragend', _ => this.setState({ dragging: false })))
  }

  cancel = _ => {
    const { mapContainer, moveListener, clickListener, downListener, skipEventListener, dragListener, markups } = this

    if (!this.props.drawingWire) return

    mapContainer.removeEventListener('mousemove', moveListener)
    mapContainer.removeEventListener('click', clickListener, true)
    mapContainer.removeEventListener('mousedown', downListener, true)
    mapContainer.removeEventListener('touchstart', downListener, true)
    mapContainer.removeEventListener('rightclick', skipEventListener, true)
    mapContainer.removeEventListener('contextmenu', skipEventListener, true)
    google.maps.event.removeListener(dragListener)
    markups.forEach(m => m.setMap(null))
    this.props.map.setOptions({ draggableCursor: '' })
    this.props.updateUiAction({ drawingWire: false })
  }

  componentWillUnmount = _ => {
    this.markerDragListeners.forEach(listener => listener && listener.remove())
    this.cancel()
  }

  activateWireInsertMode = _ => {
    const { map, drawingWire, startDevice, startDeviceType, networkClosets,
      updateUiAction, addWireAction, mobile, notificationAction } = this.props

    if (drawingWire) return

    const strokeColor = startDeviceType === 'camera' ? CAMERA_SELECTED_COLORS[startDevice.color] : startDevice.color

    const mouseFollower = new google.maps.Marker({
      icon: { ...WIRE_SNAP_ICON, path: google.maps.SymbolPath.CIRCLE },
      cursor: "url(\"https://maps.google.com/mapfiles/kml/shapes/cross-hairs_highlight.png\") 16 16, crosshair",
      position: startDevice.location,
      clickable: false,
      zIndex: 110,
      visible: false,
      map
    })

    let path = [startDevice.location]
    const wire = new google.maps.Polyline({
      ...DEFAULT_WIRE_OPTIONS,
      strokeColor,
      path,
      map
    })
    let followerPath = [startDevice.location, startDevice.location]
    const followerWire = new google.maps.Polyline({
      ...WIRE_FOLLOWER_OPTIONS,
      strokeColor,
      path: followerPath,
      visible: false,
      map
    })
    let snappedCloset
    let followersDisplayed = false

    map.setOptions({ draggableCursor: "url(\"https://maps.google.com/mapfiles/kml/shapes/cross-hairs_highlight.png\") 16 16, crosshair" })

    this.mapContainer = document.getElementById('mapContainer')
    const mapComponentRef = this.mapContainer.children[0]
    const zoomButton = document.querySelector('.gm-bundled-control')
    const projection = getMapProjection(map)

    const complete = networkCloset => {
      this.cancel()
      addWireAction(wire, { type: startDeviceType, id: startDevice.id }, { type: 'networkCloset', id: networkCloset.id })
    }

    let moveListenerFrame
    const handleMove = e => {
      const bounds = this.mapContainer.getBoundingClientRect()
      const relativePixel = { x: e.clientX - bounds.left, y: e.clientY - bounds.top }
      const snapResult = snapToClosestObject(relativePixel, projection, networkClosets)

      mouseFollower.setPosition(snapResult.position)
      if (path.length > 0) {
        followerPath[1] = snapResult.position
        followerWire.setPath(followerPath)
      }

      if (!!snappedCloset !== !!snapResult.snappedObject) {
        const color = snapResult.snappedObject ? CAMERA_SELECTED_COLORS['green'] : CAMERA_SELECTED_COLORS['blue']
        const icon = mouseFollower.getIcon()
        icon.strokeColor = color
        icon.fillColor = color
        mouseFollower.setIcon(icon)
      }
      snappedCloset = snapResult.snappedObject

      if (!followersDisplayed) {
        followersDisplayed = true
        mouseFollower.setVisible(true)
        followerWire.setVisible(true)
      }
    }

    this.moveListener = e => {
      if (moveListenerFrame) cancelAnimationFrame(moveListenerFrame)

      moveListenerFrame = requestAnimationFrame(_ => {
        handleMove(e)
        moveListenerFrame = null
      })
    }

    this.clickListener = e => {
      if (!mapComponentRef.contains(e.target)) return e.stopPropagation()
      if (skipClick || zoomButton && zoomButton.contains(e.target)) return

      const bounds = this.mapContainer.getBoundingClientRect()
      const relativePixel = { x: e.clientX - bounds.left, y: e.clientY - bounds.top }
      const snapResult = snapToClosestObject(relativePixel, projection, networkClosets)

      path.push(snapResult.position)
      wire.setPath(path)
      mouseFollower.setPosition(snapResult.position)
      followerPath = [snapResult.position, snapResult.position]
      followerWire.setPath(followerPath)

      if (snapResult.snappedObject) complete(snapResult.snappedObject)

      if (!followersDisplayed) {
        followersDisplayed = true
        mouseFollower.setVisible(true)
        followerWire.setVisible(true)
      }

      return e.stopPropagation()
    }

    let skipClick = false
    this.downListener = e => {
      if (!mapComponentRef.contains(e.target)) return
      skipClick = false
    }

    this.skipEventListener = e => e.stopPropagation()

    this.mapContainer.addEventListener('mousemove', this.moveListener)
    this.mapContainer.addEventListener('click', this.clickListener, true)
    this.mapContainer.addEventListener('mousedown', this.downListener, true)
    this.mapContainer.addEventListener('touchstart', this.downListener, true)
    this.mapContainer.addEventListener('rightclick', this.skipEventListener, true)
    this.mapContainer.addEventListener('contextmenu', this.skipEventListener, true)
    this.dragListener = google.maps.event.addListener(map, 'dragstart', e => skipClick = true)
    this.markups = [mouseFollower, followerWire, wire]

    updateUiAction({ drawingWire: true })
    if (mobile) notificationAction('Tap Network Closet to complete cabling', 'info')
  }

  connectWireDirectly = _ => {
    const { startDevice, startDeviceType, onlyNetworkCloset, map, addWireAction } = this.props

    const wire = new google.maps.Polyline({
      ...DEFAULT_WIRE_OPTIONS,
      strokeColor: startDeviceType === 'camera' ? CAMERA_SELECTED_COLORS[startDevice.color] : startDevice.color,
      path: [startDevice.location, onlyNetworkCloset.location],
      map
    })

    addWireAction(wire, { type: startDeviceType, id: startDevice.id }, { type: 'networkCloset', id: onlyNetworkCloset.id }, true)
  }

  highlightListener = event => {
    const { mobile, highlightNetworkClosetListener, updateUiAction, highlightAddNetworkCloset } = this.props

    if (mobile) {
      highlightNetworkClosetListener(event)
    } else {
      event.stopPropagation()
      updateUiAction({ highlightAddNetworkCloset: !highlightAddNetworkCloset })
    }
  }

  renderWiringInfo = _ => {
    const { activateWireInsertMode, cancel, connectWireDirectly, highlightListener,
      props: { drawingWire, onlyNetworkCloset, networkClosetExists, mobile, classes } } = this
    const wrapperClass = mobile ? classes.mobileButtonWrapper : classes.buttonWrapper

    if (drawingWire) {
      return (
        <div className={wrapperClass}>
          <em>Click Network Closet to complete</em>
          <div
            onClick={cancel}
            className={classes.cancelButton + (mobile ? ' mobile' : '')}
          >
            Cancel cabling
          </div>
        </div>
      )
    }

    if (onlyNetworkCloset) {
      return (
        <div className={wrapperClass}>
          <div
            onClick={connectWireDirectly}
            className={classes.drawWireButton + (mobile ? ' mobile' : '')}
          >
            Connect to { onlyNetworkCloset.name }
          </div>
        </div>
      )
    }

    if (networkClosetExists) {
      return (
        <div className={wrapperClass}>
          <div
            onClick={activateWireInsertMode}
            className={classes.drawWireButton + (mobile ? ' mobile' : '')}
          >
            Draw cabling to Network Closet
          </div>
        </div>
      )
    }

    return (
      <div className={wrapperClass}>
        <div
          onClick={highlightListener}
          className={classes.addNetworkClosetButton + (mobile ? ' mobile' : '')}
        >
          First, add a Network Closet
        </div>
      </div>
    )
  }

  render() {
    const { wire, startDevice, startDeviceType, networkCloset, mobile, classes } = this.props

    return (
      <div className={classes.wiringPanel}>
        <WireDiagram
          wire={wire}
          startDevice={startDevice}
          startDeviceType={startDeviceType}
          endDevice={networkCloset}
          wiringInfo={this.renderWiringInfo()}
          dragging={this.state.dragging}
          mobile={mobile}
          wiringPanel
        />
      </div>
    )
  }
}

export default connect(mapStateToProps, { addWireAction, updateUiAction, notificationAction })(injectSheet(style)(WiringPanel))



// WEBPACK FOOTER //
// ./src/components/WiringPanel/index.js